import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentMaterials, setRecentMaterials] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, notified: 0, users: 0, materials: 0, orders: 0, ordersPending: 0, enquiries: 0, reviews: 0, categories: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setError('');
    try {
      const [notificationsRes, statsRes, usersRes, materialsRes] = await Promise.all([
        axios.get('/api/admin/notifications'),
        axios.get('/api/admin/stats'),
        axios.get('/api/admin/users?limit=5'),
        axios.get('/api/admin/materials?limit=5')
      ]);

      setNotifications(notificationsRes.data);
      setStats(statsRes.data);
      setRecentUsers(usersRes.data);
      setRecentMaterials(materialsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data. Check server connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      try {
        await axios.delete(`/api/admin/notifications/${id}`);
        fetchData();
      } catch (error) {
        console.error('Error deleting notification:', error);
        alert('Error deleting notification');
      }
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-dashboard-content">
        <h2 className="admin-dashboard-title">Dashboard</h2>
        <p className="admin-dashboard-subtitle">Welcome, {user?.email}</p>
        {error && (
          <div style={{ background: '#fef2f2', color: '#dc2626', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #fecaca' }}>
            {error} <button onClick={fetchData} style={{ marginLeft: '12px', cursor: 'pointer', background: '#dc2626', color: '#fff', border: 'none', padding: '4px 12px', borderRadius: '4px' }}>Retry</button>
          </div>
        )}
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.orders ?? 0}</div>
            <div className="admin-stat-label">Total Orders</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.ordersPending ?? 0}</div>
            <div className="admin-stat-label">Pending Orders</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.materials ?? 0}</div>
            <div className="admin-stat-label">Materials</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.categories ?? 0}</div>
            <div className="admin-stat-label">Categories</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.users ?? 0}</div>
            <div className="admin-stat-label">Users</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.enquiries ?? 0}</div>
            <div className="admin-stat-label">Enquiries</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.reviews ?? 0}</div>
            <div className="admin-stat-label">Reviews</div>
          </div>
          <div className="admin-stat-card">
            <div className="admin-stat-value">{stats.total ?? 0}</div>
            <div className="admin-stat-label">Notifications</div>
          </div>
        </div>

        <div className="admin-dashboard-grid">
          <div className="admin-notifications-section">
            <h2 className="admin-section-title">Recent Users</h2>
            <div className="admin-notifications-table">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUsers.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="admin-no-data">No users yet</td>
                    </tr>
                  ) : (
                    recentUsers.map((u) => (
                      <tr key={u._id}>
                        <td>{u.email}</td>
                        <td>
                          <span className={`admin-status-badge admin-status-${u.role}`}>{u.role}</span>
                        </td>
                        <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="admin-notifications-section">
            <h2 className="admin-section-title">Recent Materials</h2>
            <div className="admin-notifications-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Category</th>
                    <th>Brand</th>
                    <th>Price</th>
                  </tr>
                </thead>
                <tbody>
                  {recentMaterials.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="admin-no-data">No materials yet</td>
                    </tr>
                  ) : (
                    recentMaterials.map((m) => (
                      <tr key={m._id}>
                        <td>{m.name}</td>
                        <td>{m.category}</td>
                        <td>{m.brand}</td>
                        <td>{m.price}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="admin-notifications-section">
          <h2 className="admin-section-title">Email Notifications</h2>
          <div className="admin-notifications-table">
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {notifications.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="admin-no-data">No notifications yet</td>
                  </tr>
                ) : (
                  notifications.map((notification) => (
                    <tr key={notification._id}>
                      <td>{notification.email}</td>
                      <td>
                        <span className={`admin-status-badge admin-status-${notification.status}`}>
                          {notification.status}
                        </span>
                      </td>
                      <td>{new Date(notification.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button
                          onClick={() => handleDelete(notification._id)}
                          className="admin-delete-button"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
